import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AudioFileOutlinedIcon from "@mui/icons-material/AudioFileOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import { toast } from "react-toastify";

import TaskButton from "../Buttons/TaskButton";
import { requestMediaDownloadUrl } from "../../utils/audioMedia";
import { getFallbackPlaybackUrl } from "../../hooks/useProjectMediaPlayback";

const KIND_ICONS = {
  video: MovieOutlinedIcon,
  audio: AudioFileOutlinedIcon,
};

const startDownload = async (reference, projectId) => {
  const downloadUrl = await requestMediaDownloadUrl(reference, projectId);
  if (downloadUrl) {
    // The URL is signed with Content-Disposition: attachment, so the browser
    // saves it. The new tab keeps the QA page open if a backend without
    // download support returns a plain playback URL instead.
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  // Legacy public references cannot be signed; open them for a manual save.
  const fallbackUrl = getFallbackPlaybackUrl(reference, projectId);
  if (!fallbackUrl) {
    throw new Error("No download URL available");
  }
  window.open(fallbackUrl, "_blank", "noopener");
};

const groupItems = (items) =>
  items.reduce((groups, item) => {
    const group = groups.find(({ name }) => name === item.group);
    if (group) {
      group.items.push(item);
    } else {
      groups.push({ name: item.group, items: [item] });
    }
    return groups;
  }, []);

const MediaDownloadMenu = ({
  projectId,
  items,
  label = "Download",
  renderTrigger,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingKey, setPendingKey] = useState(null);
  const availableItems = items.filter((item) => item.reference);
  const groups = groupItems(items);
  const triggerProps = {
    onClick: (event) => setAnchorEl(event.currentTarget),
    disabled: !projectId || availableItems.length === 0,
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(anchorEl),
  };

  const handleDownload = async (item) => {
    setPendingKey(item.key);
    try {
      await startDownload(item.reference, projectId);
      setAnchorEl(null);
    } catch (error) {
      console.error("Error downloading media:", error);
      toast.error(`Could not download ${item.label}`);
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <TaskButton
          {...triggerProps}
          width="200px"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <DownloadIcon fontSize="small" />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          <ArrowDropDownIcon fontSize="small" />
        </TaskButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true, sx: { minWidth: 280, py: 0 } }}
      >
        {groups.flatMap((group, groupIndex) =>
          [
            groupIndex > 0 && <Divider key={`${group.name}-divider`} />,
            <ListSubheader key={`${group.name}-header`}>
              {group.name}
            </ListSubheader>,
            ...group.items.map((item) => {
              const Icon = KIND_ICONS[item.kind] || DownloadIcon;
              return (
                <MenuItem
                  key={item.key}
                  onClick={() => handleDownload(item)}
                  disabled={!item.reference || pendingKey !== null}
                >
                  <ListItemIcon>
                    {pendingKey === item.key ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Icon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={
                      item.reference ? item.description : "Not available"
                    }
                  />
                </MenuItem>
              );
            }),
          ].filter(Boolean)
        )}
      </Menu>
    </>
  );
};

MediaDownloadMenu.propTypes = {
  projectId: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(["video", "audio"]),
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      reference: PropTypes.string,
    })
  ).isRequired,
  label: PropTypes.string,
  // Optional custom trigger; receives the button props (onClick, disabled, aria-*).
  renderTrigger: PropTypes.func,
};

export default MediaDownloadMenu;
